import { useState } from "react";
import { format } from "date-fns";
import { AlertTriangle, Paperclip, Send, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import {
  useAttachToDraft,
  useDiscardDraft,
  useSendDraft,
  type EopDraft,
} from "@/features/eop_mail/api/mailbox";
import { sanitizeMailHtml, extractError } from "./sanitize";

const STATUS: Record<EopDraft["status"], { label: string; color: "gray" | "green" | "red" | "orange" }> = {
  draft: { label: "czeka na wysyłkę", color: "orange" },
  sent: { label: "wysłana", color: "green" },
  discarded: { label: "porzucona", color: "gray" },
  failed: { label: "błąd wysyłki", color: "red" },
};

/**
 * Kopia robocza z jedynym w całym systemie przyciskiem, który coś wysyła z eop@.
 *
 * Świadomie nie ma tu „wyślij i zamknij", „wyślij wszystkie" ani skrótu
 * klawiszowego: to jest ten moment, w którym nazwisko klikającego trafia pod
 * korespondencję do operatora, i ma kosztować jedno pełne kliknięcie.
 */
export function DraftCard({ draft }: { draft: EopDraft }) {
  const send = useSendDraft();
  const discard = useDiscardDraft();
  const attach = useAttachToDraft();
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);

  const status = STATUS[draft.status];
  const editable = draft.status === "draft" || draft.status === "failed";

  return (
    <div className="space-y-2 border border-[var(--color-border)] p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge color={status.color}>{status.label}</Badge>
        <span className="text-[13px] font-medium text-[var(--color-text-primary)]">
          {draft.subject || "(bez tematu)"}
        </span>
        <span className="ml-auto text-[11px] text-[var(--color-text-muted)]">
          {draft.created_by_name || "—"} · {format(new Date(draft.created_at), "dd.MM.yyyy HH:mm")}
        </span>
      </div>

      <div className="text-[11px] text-[var(--color-text-muted)]">
        <span className="section-label mr-2">Do</span>
        {draft.to_addresses.join(", ") || "— brak adresata"}
        {draft.cc_addresses.length > 0 && (
          <>
            <span className="section-label ml-3 mr-2">DW</span>
            {draft.cc_addresses.join(", ")}
          </>
        )}
      </div>

      {draft.body_html && (
        <div
          className="rich-text-content max-h-80 overflow-y-auto border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-3 text-[13px] leading-relaxed [&_hr]:my-3 [&_p]:my-1"
          dangerouslySetInnerHTML={{ __html: sanitizeMailHtml(draft.body_html) }}
        />
      )}

      {(draft.attachments_meta?.length ?? 0) > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {draft.attachments_meta.map((file, index) => (
            <span
              key={`${file.name}-${index}`}
              className="inline-flex items-center gap-1 border border-[var(--color-border)] px-2 py-0.5 text-[11px] text-[var(--color-text-secondary)]"
            >
              <Paperclip size={11} />
              {file.name}
              <span className="text-[var(--color-text-muted)]">
                {Math.max(1, Math.round(file.size / 1024))} kB
              </span>
            </span>
          ))}
        </div>
      )}

      {editable && (
        <FileDropZone
          multiple
          label={attach.isPending ? "Wysyłanie załącznika…" : "Dodaj załącznik (maks. 25 MB)"}
          disabled={attach.isPending}
          onSelect={(files) =>
            files.forEach((file) =>
              attach.mutate(
                { id: draft.id, file },
                { onError: (err) => setError(extractError(err)) },
              ),
            )
          }
        />
      )}

      {draft.touched_in_outlook && (
        <p className="flex items-start gap-1 text-[11px] text-[var(--color-text-muted)]">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          Ktoś zmienił ten szkic w Outlooku, więc został w skrzynce — system
          przestał się nim zajmować, ale go nie skasował.
        </p>
      )}

      {draft.send_error && (
        <p className="text-[11px] text-[var(--color-danger)]">{draft.send_error}</p>
      )}
      {error && <p className="text-[11px] text-[var(--color-danger)]">{error}</p>}

      {draft.status === "sent" && (
        <p className="text-[11px] text-[var(--color-text-muted)]">
          Wysłał(a): <strong>{draft.sent_by_name || "—"}</strong>
          {draft.sent_at && ` · ${format(new Date(draft.sent_at), "dd.MM.yyyy HH:mm")}`}
          {" · "}mail wyszedł z adresu eop@elektim.pl
        </p>
      )}

      {editable && (
        <div className="flex flex-wrap items-center gap-2">
          {confirming ? (
            <>
              <span className="text-[11px] text-[var(--color-text-primary)]">
                Wysłać do: {draft.to_addresses.join(", ") || "—"}?
              </span>
              <Button
                size="sm"
                loading={send.isPending}
                onClick={() =>
                  send.mutate(draft.id, {
                    onError: (err) => {
                      setError(extractError(err));
                      setConfirming(false);
                    },
                    onSuccess: () => setConfirming(false),
                  })
                }
              >
                <Send size={13} className="mr-1" />
                Tak, wyślij
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                Wróć
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                onClick={() => setConfirming(true)}
                disabled={draft.to_addresses.length === 0}
              >
                <Send size={13} className="mr-1" />
                Wyślij
              </Button>
              <Button
                variant="ghost"
                size="sm"
                loading={discard.isPending}
                onClick={() =>
                  discard.mutate(draft.id, {
                    onError: (err) => setError(extractError(err)),
                  })
                }
              >
                <Trash2 size={13} className="mr-1" />
                Porzuć
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
